import { useCallback, useEffect, useRef } from 'react';
import { problemDetailsSchema } from '@hf/sharedContract';
import { useHFAlert } from '@hf/designSystem/v1';
import { useAccount } from '../auth/AccountContext';
import { apiUrl } from './apiUrl';

export interface HttpResult<T> {
  status: number;
  data: T | null;
}

type SchemaParse<T> = {
  safeParse: (input: unknown) => { success: true; data: T } | { success: false };
};

export type HttpRequestInit<TBody = unknown, TResponse = unknown> = Omit<RequestInit, 'body'> & {
  json?: TBody;
  body?: BodyInit | null;
  requestSchema?: SchemaParse<TBody>;
  responseSchema?: SchemaParse<TResponse>;
  /** When false, failed responses are not auto-toasted (caller handles UX). Default true. */
  toastError?: boolean;
  /**
   * How to dedupe in-flight calls.
   * - `route` (default): same method+path aborts the prior call, ignoring query (fund/date switches).
   * - `url`: include query so parallel fan-out for different fundIds can run together.
   */
  coalesce?: 'route' | 'url';
};

function inflightKey(method: string, path: string, coalesce: 'route' | 'url'): string {
  const route = coalesce === 'url' ? path : (path.split('?')[0] ?? path);
  return `${method.toUpperCase()}:${route}`;
}

function mergeSignals(internal: AbortSignal, external?: AbortSignal | null): AbortSignal {
  if (!external) return internal;
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([internal, external]);
  if (internal.aborted || external.aborted) {
    const aborted = new AbortController();
    aborted.abort();
    return aborted.signal;
  }
  const merged = new AbortController();
  const forward = () => merged.abort();
  internal.addEventListener('abort', forward, { once: true });
  external.addEventListener('abort', forward, { once: true });
  return merged.signal;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException ? error.name === 'AbortError' : error instanceof Error && error.name === 'AbortError';
}

export function apiErrorMessage(status: number, body: unknown): string {
  const parsed = problemDetailsSchema.safeParse(body);
  if (parsed.success) return parsed.data.detail ?? parsed.data.title;
  if (status === 401) return 'Session expired';
  if (status === 403) return 'You do not have access';
  if (status === 400) return 'Invalid request';
  return 'Unable to reach the server';
}

export function useHttp() {
  const { user, getJwtToken, refreshIfNeeded, expireSession } = useAccount();
  const { push } = useHFAlert();
  const inflight = useRef(new Map<string, AbortController>());
  const userRef = useRef(user);
  userRef.current = user;
  const getJwtTokenRef = useRef(getJwtToken);
  getJwtTokenRef.current = getJwtToken;
  const refreshIfNeededRef = useRef(refreshIfNeeded);
  refreshIfNeededRef.current = refreshIfNeeded;
  const expireSessionRef = useRef(expireSession);
  expireSessionRef.current = expireSession;
  const pushRef = useRef(push);
  pushRef.current = push;

  useEffect(
    () => () => {
      for (const controller of inflight.current.values()) controller.abort();
      inflight.current.clear();
    },
    [],
  );

  const request = useCallback(async <T>(path: string, init: HttpRequestInit<unknown, T> = {}): Promise<HttpResult<T>> => {
    const { json, requestSchema, responseSchema, toastError = true, coalesce = 'route', headers: initHeaders, signal: userSignal, ...rest } = init;
    const method = typeof rest.method === 'string' ? rest.method : 'GET';
    const key = inflightKey(method, path, coalesce);

    inflight.current.get(key)?.abort();
    const controller = new AbortController();
    inflight.current.set(key, controller);

    let body = rest.body;
    if (json !== undefined) {
      const parsed = requestSchema ? requestSchema.safeParse(json) : { success: true as const, data: json };
      if (!parsed.success) {
        if (inflight.current.get(key) === controller) inflight.current.delete(key);
        return { status: 400, data: null };
      }
      body = JSON.stringify(parsed.data);
    }

    try {
      // Never send a known-expired access token — refresh first, or skip the call after logout.
      let token = getJwtTokenRef.current();
      if (userRef.current != null) {
        token = await refreshIfNeededRef.current();
        if (controller.signal.aborted || userSignal?.aborted) return { status: 0, data: null };
        if (!token) {
          if (toastError) pushRef.current({ severity: 'error', message: apiErrorMessage(401, null) });
          return { status: 401, data: null };
        }
      }

      const response = await fetch(apiUrl(path), {
        ...rest,
        method,
        body,
        signal: mergeSignals(controller.signal, userSignal),
        headers: {
          'Content-Type': 'application/json',
          ...(initHeaders ?? {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (response.status === 401) {
        expireSessionRef.current();
      }

      const raw: unknown = response.status === 204 ? null : await response.json().catch(() => null);
      if (!response.ok && toastError) {
        pushRef.current({ severity: 'error', message: apiErrorMessage(response.status, raw) });
      }
      if (response.ok && responseSchema && raw !== null) {
        const parsed = responseSchema.safeParse(raw);
        if (!parsed.success) {
          // Keep Network green (real HTTP was OK) but surface a console clue — callers with toastError:false
          // previously looked like a silent "fund failed" toast with no failed requests.
          console.warn(`[http] invalid response ${method} ${path}`, 'error' in parsed ? parsed.error : parsed);
          if (toastError) pushRef.current({ severity: 'error', message: 'Invalid response from server' });
          return { status: 502, data: null };
        }
        return { status: response.status, data: parsed.data };
      }
      return { status: response.status, data: raw as T | null };
    } catch (error) {
      if (controller.signal.aborted || isAbortError(error)) return { status: 0, data: null };
      if (toastError) pushRef.current({ severity: 'error', message: apiErrorMessage(0, null) });
      return { status: 0, data: null };
    } finally {
      if (inflight.current.get(key) === controller) inflight.current.delete(key);
    }
  }, []);

  return { request };
}
